import { useMemo } from "react"

import { GatsbyImage } from "gatsby-plugin-image"
import { useI18next } from "gatsby-plugin-react-i18next"
import styled, { css } from "styled-components"

import InnerWrapper from "components/InnerWrapper"
import Callout from "components/TextElements/Callout"
import pathsPNG from "images/homepage/pathCTABanner.svg"
import pathsPNGTablet from "images/homepage/pathCTABannerTablet.svg"
import pathsPNGMobile from "images/homepage/pathsPNGMobile.svg"
import colors from "styles/colors"
import media from "styles/media"
import text from "styles/text"
import { ButtonProps } from "types"
import { CustomCTAData } from "types/gatsby-alias"
import { fmobile, fresponsive, ftablet } from "utils/fullyResponsive"
import UniversalLink from "utils/Loader/UniversalLink"
import { checkedLocaleLink } from "utils/routes"
import useMedia from "utils/useMedia"


interface Props {
  bannerData: CustomCTAData
  className?: string
}

export default function ContentfulCTA({ bannerData, className = "" }: Props) {
  const {
    smallImage,
    soloImage,
    title,
    text: bannerText,
    buttons,
  } = bannerData ?? {}
  
  const { language } = useI18next()
  const soloImageData = useMedia(
    soloImage?.images?.[0]?.gatsbyImageData,
    soloImage?.images?.[0]?.gatsbyImageData,
    soloImage?.images?.[0]?.gatsbyImageData,
    soloImage?.images?.[1]?.gatsbyImageData
  )

  const titleWidth = useMedia("40vw", "50.855vw", "62.988vw", "89.333vw")
  const imgWidth = useMedia("63.855vw", "63.855vw", "64.746vw", "90.4vw")
  const titleSize = useMedia(text.h3, text.h3, text.h3, text.h1Mobile)
  const textSize = useMedia(text.bodyR, text.bodyR, text.bodyR, text.bodyS)
  const bannerImg = useMedia(pathsPNG, pathsPNG, pathsPNGTablet, pathsPNGMobile)
  const stackedVal = useMedia(false, false, false, true)

  const allButtons = useMemo(() => {  
    if (buttons) {
      return buttons.map(button => {
        const buttonType =
          (button?.typeOverride && button.typeOverride[0]) ?? "primary"

        const canHaveIcon =
          buttonType === "primary" || buttonType === "secondary"

        const isLink = buttonType === "link"
        const linkUrl = button?.url ? checkedLocaleLink(button.url, language) : undefined;
        return {
          text: button?.buttonText ?? undefined,
          route: linkUrl,
          lightMode: canHaveIcon && button?.lightMode && button.lightMode,
          download: true,
          arrow: false,
          type: buttonType,
          gradient: isLink
            ? {
                gradient: "white",
                fill: "white",
              }
            : null,
        }
      })
    }
  }, [buttons, language])

  return (
    <CalloutBanner className={className} backgroundOn={!soloImage}>
      {!soloImage ? (
        <>
          <Paths
            imgWidth={imgWidth}
            src={bannerImg}
            alt="line art"
            loading="lazy"
          />
          <InnerWrapperStyled className="COBanner_Inner">
            <Callout
              stacked={stackedVal}
              variant="white"
              title={{
                content: title ?? "",
                size: titleSize,
                width: titleWidth,
              }}
              buttons={allButtons as ButtonProps[]}
            />
            {smallImage?.gatsbyImageData && (
              <ImageWrapper>
                <Image
                  image={smallImage.gatsbyImageData}
                  loading="lazy"
                  alt={smallImage.description ?? "no description provided"}
                />
              </ImageWrapper>
            )}
          </InnerWrapperStyled>
        </>
      ) : (
        <UniversalLink to={soloImage.imageUrl ?? ""}>
          {soloImageData && (
            <SoloImage
              loading="lazy"
              image={soloImageData}
              alt={
                soloImage.images?.[0]?.description ??
                soloImage.images?.[1]?.description ??
                "no description provided"
              }
            />
          )}
        </UniversalLink>
      )}
    </CalloutBanner>
  )
}

const CalloutBanner = styled.div<{ backgroundOn: boolean }>`
  width: 100%;
  min-height: 16.096vw;
  background: ${colors.blueBabyHorz};
  position: relative;
  display: flex;
  justify-content: center;
  padding: 5.542vw 0 5.241vw 0;

  ${media.fullWidth} {
    padding: 92px 0 87px 0;
    height: auto;
    min-height: 16vw;
  }
  ${media.tablet} {
    background: ${({ backgroundOn }) =>
      backgroundOn ? colors.blueBabyHorz : "transparent"};
    padding: ${({ backgroundOn }) => (backgroundOn ? "7.813vw" : 0)};
  }
  ${media.mobile} {
    background: ${({ backgroundOn }) =>
      backgroundOn ? colors.blueBabyHorz : "transparent"};
    padding: ${({ backgroundOn }) => (backgroundOn ? "7.813vw 0 26.667vw" : 0)};
  }
`

const Paths = styled.img<{
  imgWidth: string | undefined
}>`
  position: absolute;
  filter: contrast(150%);
  bottom: 0;
  right: 0;
  width: ${({ imgWidth }) => imgWidth};
`

const InnerWrapperStyled = styled(InnerWrapper)`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${media.tablet} {
    flex-direction: column-reverse;
    justify-content: flex-start;
    gap: 7.813vw;
  }
  ${media.mobile} {
    flex-direction: column-reverse;
    justify-content: flex-start;
    gap: 7.813vw;
  }
`

const ImageWrapper = styled.div`
  position: relative;
  height: fit-content;
  display: flex;

  ${fresponsive(css`
    width: 517px;
    border-radius: 20px;
  `)}

  ${ftablet(css`
    width: 832px;
  `)}

  ${fmobile(css`
    width: 345px;
  `)}
`

const Image = styled(GatsbyImage)`
  height: auto;

  ${fresponsive(css`
    max-width: 517px;
    border-radius: 20px;
  `)}

  ${ftablet(css`
    max-width: 832px;
  `)}

  ${fmobile(css`
    max-width: 345px;
  `)}
`

const SoloImage = styled(GatsbyImage)`
  ${fresponsive(css`
    width: 1440px;
    height: 809px;
  `)}

  ${ftablet(css`
    width: 100%;
    height: 576px;
  `)}

  ${fmobile(css`
    width: 100%;
    height: 750px;
  `)}
`
